import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileDownload, faPencil, faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, FormField, Grid, Text,
} from '@domain-group/suit-theme'
import { ArtworkDownload } from '@domain-group/artwork-builder'

import useArtwork from '@hooks/useArtwork'
import useRedirectUrlWithCacheKey from '@hooks/useRedirectUrlWithCacheKey'

import type { ValidateOrderItemError } from '@functions/orderItem'

import type { UseOrderItemPayload } from '@hooks/useOrderItem'
import type { UseOrderApprovalPayload } from '@modals/ApproveOrderModal/hooks/useOrderApproval'

import Errors from '../../Errors'
import ItemHeader from '../ItemHeader'

import ArtworkPage from './ArtworkPage'
import ReviewStatus from './ReviewStatus'

interface ArtworkProps {
  errors: ValidateOrderItemError[],
  orderItemPayload: UseOrderItemPayload,
  orderApprovalPayload: UseOrderApprovalPayload,
}

const Artwork = (props: ArtworkProps) => {
  const {
    errors,
    orderItemPayload: {
      artwork,
      callbacks: { createArtwork, manageOrderItem },
      canCreateArtwork,
      canDownloadPrintQualityArtwork,
      orderItem,
    },
    orderApprovalPayload: {
      callbacks: { removeDownloadedArtworkId },
    },
  } = props

  const { redirectUrl } = useRedirectUrlWithCacheKey([orderItem.id])

  const artworkPayload = useArtwork(artwork, {
    generateUrlsOptions: {
      includeRedirect: true,
      redirectUrl,
    },
    performHttpRequests: false,
  })

  const {
    artwork: { canva_id },
    filteredArtworkPages,
    isUploadedArtwork,
    urls: { editArtworkUrl },
  } = artworkPayload

  return (
    <Box borderTop="1px solid" borderColor="lineColor" flexDirection="column" padding="large">
      <ItemHeader errors={errors} title="Artwork" />

      <Errors errors={errors} />

      {canCreateArtwork && !artwork.id && (
        <Box
          alignItems="center"
          border="1px dashed"
          borderColor="lineColor"
          justifyContent="center"
          marginTop="large"
          padding="large"
        >
          <Button
            buttonStyle="secondaryCreate"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => createArtwork()}
            // @ts-expect-error
            marginRight="small"
            size="medium"
            width="auto"
          >
            Create Artwork
          </Button>
        </Box>
      )}

      {!!filteredArtworkPages.length && (
        <FormField label="Pages" marginTop="large">
          <Grid
            // @ts-expect-error TS2322
            gridAutoRows="1fr"
            gridGap={4}
            gridTemplateColumns="repeat(auto-fill, minmax(160px, 1fr))"
            gridTemplateRows="repeat(auto-fill, minmax(160px, 1fr))"
          >
            {filteredArtworkPages.map(artworkPage => (
              <ArtworkPage key={artworkPage.id} artworkPage={artworkPage} />
            ))}
          </Grid>
        </FormField>
      )}

      {!!artwork.id && (
        <>
          <Text color="lightGrey" fontSize="xsmall" lineHeight="1.3" marginTop="large">
            Please download the artwork before approving to check for any changes or errors. Please note that Print
            Quality files include bleed that will be trimmed during production. The Web Quality shows an estimate of the
            final trimmed output.
          </Text>

          <Box
            borderTop="1px dashed"
            borderColor="lineColor"
            flexDirection={['column', 'row']}
            justifyContent="space-between"
            marginTop="large"
            paddingTop="large"
          >
            <Box width={['100%', 'auto']}>
              <ReviewStatus
                artworkPayload={artworkPayload}
                callbacks={{ manageOrderItem }}
              />
            </Box>

            <Box flexDirection={['column', 'row']} width={['100%', 'auto']}>
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="secondaryUtility"
                href={editArtworkUrl}
                target={canva_id ? '_blank' : undefined}
                icon={<FontAwesomeIcon icon={faPencil} />}
                marginBottom={['small', 0]}
                marginRight="small"
                size="medium"
                width={['100%', 'auto']}
              >
                Edit Artwork
              </Button>

              {canDownloadPrintQualityArtwork && (
                <ArtworkDownload
                  artwork={artwork}
                  callbacks={{
                    afterAction: () => {
                      // @ts-expect-error TS2339
                      removeDownloadedArtworkId(artwork.id)
                    },
                  }}
                  key={canDownloadPrintQualityArtwork ? 'print' : 'web'}
                  queryParams={{ low_res: !(canDownloadPrintQualityArtwork || isUploadedArtwork) }}
                >
                  <Button
                    // @ts-expect-error TS2322
                    as="a"
                    buttonStyle="secondaryEdit"
                    icon={<FontAwesomeIcon icon={faFileDownload} />}
                    size="medium"
                    width={['100%', 'auto']}
                  >
                    Download Artwork Preview
                  </Button>
                </ArtworkDownload>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default Artwork
